import { useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ServerReachabilityStatus } from '../../useServerReachabilityCheck';
import { SyncRecordBase } from '../persistence';
import { Syncer } from './syncItemBatch';
import { syncItemsUntilDone } from './syncItemsUntilDone';
import { useSyncWorker } from './useSyncWorker';

const SUBSCRIPTION_DEBOUNCE_MS = 2000;
const SUBSCRIPTION_MAX_WAIT_MS = 5000;

/**
 * Subscribes to the model and starts the sync loop when the model changes.
 *
 * With the help of runSyncLoop and useSyncWorker:
 *
 * 1. ensures only one instance of the sync loop is running at a time
 * 2. exponential backoff on errors
 * 3. auto runs when enabled transitions from false to true
 * 4. debounces subscription triggers (500ms idle / 5s max) so batch-queued records don't start N sync loops
 */
export const useSyncWorkerWithSubscription = <T extends SyncRecordBase>({ syncer, enabled, checkServerReachability }: {
  enabled: boolean;
  syncer: Syncer<T>;
  checkServerReachability: () => Promise<ServerReachabilityStatus>;
}) => {
  const { start, isRunning } = useSyncWorker(async () => syncItemsUntilDone(syncer, checkServerReachability), enabled);

  const debouncedStart = useDebouncedCallback(start, SUBSCRIPTION_DEBOUNCE_MS, {
    leading: false,
    trailing: true,
    maxWait: SUBSCRIPTION_MAX_WAIT_MS,
  });

  useEffect(() => {
    const unsubscribe = syncer.model.subscribe(debouncedStart);
    return () => {
      unsubscribe();
      debouncedStart.cancel();
    };
  }, [syncer.model, debouncedStart]);

  return { start, isRunning };
};
